import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useCurrentProject } from '../../../../hooks/api/projects/useCurrentProject';
import releaseGroupSelectors from '../../../../redux/project_grouping/selectors';
import { checkProjectScope } from '../types';
import type { ProjectType } from '../../../../../typings/enums';
import type { InventoryScope, UseInventoryFeaturesResult } from '../types';

import { useEnhancedScanFeaturesAvailable } from './useEnhancedScanFeaturesAvailable';

/**
 * A hook for figuring out which features of the inventory dropdown should be shown.
 *
 * @param {InventoryScope} scope - The scope of the inventory dropdown.
 *
 */
export function useInventoryFeatures(scope: InventoryScope): UseInventoryFeaturesResult {
  const isProjectScope = checkProjectScope(scope);

  //  Note: that this part relies on the Release Groups Redux state
  const release = useSelector(releaseGroupSelectors.getRelease);
  const releaseProjectTypes = useMemo(() => {
    if (release) {
      return new Set<ProjectType>(release.projects.map(p => p.project.type));
    }

    return new Set<ProjectType>();
  }, [release]);

  const project = useCurrentProject();
  const projectType = useMemo(() => {
    if (project.data?.type) {
      return new Set<ProjectType>([project.data.type]);
    }

    return new Set<ProjectType>();
  }, [project.data?.type]);

  const availableProjectTypes = isProjectScope ? projectType : releaseProjectTypes;
  const {
    areSnippetsAvailable,
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
  } = useEnhancedScanFeaturesAvailable(availableProjectTypes);

  // Only consider True for snippets if we're looking at a project, since RG snippets are not supported yet.
  const shouldShowSnippets = isProjectScope && areSnippetsAvailable;
  // Memoize these two results because we don't want to iterate release.projects on every render.
  // Vendored dependencies are available for both project and release group scopes
  const shouldShowVendored = areVendoredDependenciesAvailable;
  const shouldShowBinaryAnalysis = isBinaryAnalysisAvailable;

  const shouldShowAnyGatedFeatures = shouldShowSnippets || shouldShowBinaryAnalysis || shouldShowVendored;
  //  They need to have at least one of the gated features enabled to show the inventory dropdown
  const shouldShowInventory = isInventoryUIFeatureEnabled && shouldShowAnyGatedFeatures;

  return useMemo(() => ({
    shouldShowInventory,
    shouldShowSnippets,
    shouldShowBinaryAnalysis,
    shouldShowVendored,
  }), [shouldShowInventory, shouldShowSnippets, shouldShowBinaryAnalysis, shouldShowVendored]);
}
